/**
 * Logger utility for consistent logging across the application
 * Automatically filters out logs in production builds
 */

const isDevelopment = import.meta.env.DEV;

/**
 * Performance monitoring utilities (development only)
 */
const performanceMetrics: Map<string, number> = new Map();

/**
 * Logs a message (only in development)
 */
export const logger = {
  log: (...args: any[]) => {
    if (isDevelopment) {
      console.log('[LOG]', ...args);
    }
  },

  info: (...args: any[]) => {
    if (isDevelopment) {
      console.info('[INFO]', ...args);
    }
  },

  warn: (...args: any[]) => {
    if (isDevelopment) {
      console.warn('[WARN]', ...args);
    }
  },

  error: (...args: any[]) => {
    // Always log errors, even in production
    console.error('[ERROR]', ...args);
  },

  debug: (...args: any[]) => {
    if (isDevelopment) {
      console.debug('[DEBUG]', ...args);
    }
  },

  /**
   * Start performance timing for a given label
   */
  timeStart: (label: string) => {
    if (isDevelopment && typeof performance !== 'undefined' && performance.mark) {
      performance.mark(`${label}-start`);
      performanceMetrics.set(label, performance.now());
    }
  },

  /**
   * End performance timing and log the duration
   */
  timeEnd: (label: string) => {
    if (isDevelopment && typeof performance !== 'undefined' && performance.mark) {
      const startTime = performanceMetrics.get(label);
      if (startTime !== undefined) {
        const duration = performance.now() - startTime;
        performance.mark(`${label}-end`);
        performance.measure(label, `${label}-start`, `${label}-end`);
        console.log(`[PERF] ${label}: ${duration.toFixed(2)}ms`);
        performanceMetrics.delete(label);
      }
    }
  },

  /**
   * Measure Firestore listener count (development only)
   */
  logListenerCount: (collectionName: string, count: number) => {
    if (isDevelopment) {
      console.debug(`[PERF] Firestore listeners for ${collectionName}: ${count}`);
    }
  }
};

export default logger;

